import { forEach } from "lodash";
import { isPlatform } from "@ionic/react";
import { createContext, FC, useContext, useEffect, useMemo, useState } from "react";
import { useUsername } from "./hooks";
import { SubscriptionService } from "../services/subscriptionService";
import "cordova-plugin-purchase";
import * as Sentry from "@sentry/react";
import { getCurrentUsername } from "../utils/username";
import { JSONProduct, ProductType, r6Products } from "../services/iapProductList";

export type { JSONProduct };

// define types badly exported by the library
enum LogLevel {
    /** Disable all logging (default) */
    QUIET = 0,
    /** Show only error messages */
    ERROR = 1,
    /** Show warnings and errors */
    WARNING = 2,
    /** Also show information messages */
    INFO = 3,
    /** Enable internal debugging messages. */
    DEBUG = 4,
}
// ProductType moved to services/iapProductList.ts so deviceReceiptService
// and InAppPurchaseStore reference the same definition (imported above).
enum Platform {
    /** Apple AppStore */
    APPLE_APPSTORE = "ios-appstore",
    /** Google Play */
    GOOGLE_PLAY = "android-playstore",
    /** Windows Store */
    WINDOWS_STORE = "windows-store-transaction",
    /** Braintree */
    BRAINTREE = "braintree",
    /** Test platform */
    TEST = "test",
}

const getIapPlatform = (product: JSONProduct): Platform => {
    switch (product.platform) {
        case "ios":
            return Platform.APPLE_APPSTORE;
        case "android":
            return Platform.GOOGLE_PLAY;
        default:
            console.log("Unknown iap product platform", product.platform);
            return Platform.TEST;
    }
};

let initHasRun = false;
export const initIAPStore = async () => {
    if (initHasRun) return;
    initHasRun = true;

    if (!CdvPurchase || !CdvPurchase.store || !LogLevel) {
        console.error("Store is not defined");
        Sentry.captureMessage(
            "initIAPStore - Store is not defined when trying to be initialised"
        );
        return;
    }
    const store = CdvPurchase.store;
    store.verbosity = LogLevel.DEBUG;
    if (!process.env["REACT_APP_PURCHASE_VALIDATION"]) {
        throw "No Validation URL provided";
    }
    store.validator = process.env["REACT_APP_PURCHASE_VALIDATION"] || "";

    store.error((err: any) => {
        console.error("Store Error " + JSON.stringify(err));
    });

    const _purchaseVerified = function (receipt: CdvPurchase.VerifiedReceipt) {
        receipt
            .finish()
            .then(() => {
                const ss = SubscriptionService.getInstance();
                ss.refreshSubscription();
            })
            .catch((err) => {
                Sentry.addBreadcrumb({
                    category: "purchase",
                    message: `Error verifying purchase ${err}`,
                });
            });
    };

    const registerProduct = (iapProduct: JSONProduct) => {
        if (store.get(iapProduct.name)) return;

        // Add product to store
        store.register({
            id: iapProduct.name,
            type: ProductType.PAID_SUBSCRIPTION,
            platform: getIapPlatform(iapProduct),
        });
    };
    const initProducts = (products: JSONProduct[]) => {
        console.log("INIT Products");
        forEach(products, registerProduct);
    };

    initProducts(await r6Products);

    store
        .when()
        .approved((transaction) => {
            transaction.verify();
        })

        .verified((receipt) => {
            _purchaseVerified(receipt);
        });

    store.initialize();
    store.update();
};

const isNativePlatform = () =>
    isPlatform("ios") || (isPlatform("android") && !isPlatform("mobileweb"));

const isWebDevelopment = () =>
    !isNativePlatform() && process.env.NODE_ENV === "development";

export const useProductStore = () => {
    const username = useUsername();
    const [deviceReady, setDeviceReady] = useState(false);
    const [prods, setProds] = useState<JSONProduct[]>([]);

    useEffect(() => {
        const listener = async function () {
            setDeviceReady(true);
        };

        document.addEventListener("deviceready", listener, false);

        return () => {
            document.removeEventListener("deviceready", listener, false);
        };
    }, [document]);

    useEffect(() => {
        console.log("IAP - Use effect called");
        if (deviceReady && username) {
            console.log("IAP - Device ready and username set");
            initIAPStore();
            initR6Products();
        } else if (isWebDevelopment() && username) {
            // Dev only: on web/browser, deviceready never fires — load products directly
            console.log("IAP - Web dev platform, loading products without device ready");
            initR6Products();
        }
    }, [deviceReady, username]);

    const initR6Products = async () => {
        setProds(await r6Products);
    };

    return prods;
};

type StoreProviderType = {
    products: JSONProduct[];
    refresh: (() => Promise<any>) | null;
    store: any | null;
};

const storeProvider: StoreProviderType = {
    products: [],
    refresh: null,
    store: null,
};

export const InAppPurchaseStoreContext =
    createContext<StoreProviderType>(storeProvider);

export const useIAPStoreContext = () => {
    return useContext(InAppPurchaseStoreContext);
};

export const InAppPurchaseStoreProvider: FC<{}> = ({ children }) => {
    const products = useProductStore();
    const userId = useUsername();

    const nativeStore = isNativePlatform() && typeof CdvPurchase !== "undefined"
        ? CdvPurchase.store
        : null;

    // Dev only: mock store for web development with sample pricing
    const mockStore = useMemo(() => {
        if (nativeStore || !isWebDevelopment()) return null;
        const mockPricing: Record<string, any> = {};
        (products || []).forEach((p) => {
            const price = p.period === "year" ? "£59.99" : "£8.99";
            const priceMicros = p.period === "year" ? 59990000 : 8990000;
            mockPricing[p.name] = {
                id: p.name,
                offers: [
                    {
                        pricingPhases: [
                            { price, priceMicros, currency: "GBP" },
                        ],
                    },
                ],
            };
        });
        return {
            products: Object.values(mockPricing),
            get: (id: string) => mockPricing[id] || null,
            update: () => Promise.resolve(),
        };
    }, [nativeStore, products]);

    const store = nativeStore || mockStore;

    useEffect(() => {
        if (userId && nativeStore) {
            nativeStore.applicationUsername = getCurrentUsername;
        } else if (userId && typeof CdvPurchase !== "undefined") {
            document.addEventListener("deviceready", function () {
                CdvPurchase.store.applicationUsername = getCurrentUsername;
            });
        }
    }, [userId, nativeStore]);

    return (
        <InAppPurchaseStoreContext.Provider
            value={{
                products: products || [],
                refresh: store ? () => store.update() : null,
                store: store,
            }}
        >
            {children}
        </InAppPurchaseStoreContext.Provider>
    );
};
